import { Result, err, ok } from "@jace/api";
import { unwrapOr } from "@zetalabs/core";

class LruParseJsonError extends Error {
	constructor() {
		super("Failed to parse JSON in LRUCache");
	}
}

export class LRUCache<K extends string, V> {
	private cache: Map<K, V>;
	private capacity: number;

	constructor(capacity: number) {
		this.cache = new Map();
		this.capacity = capacity;
	}

	get(key: K): V | undefined {
		if (!this.cache.has(key)) return undefined;

		const value = this.cache.get(key)!;
		this.cache.delete(key);
		this.cache.set(key, value);
		return value;
	}

	set(key: K, value: V): void {
		if (this.cache.has(key)) {
			this.cache.delete(key); // Remove old to refresh order
		} else if (this.cache.size >= this.capacity) {
			const oldestKey = this.cache.keys().next().value;

			if (oldestKey) {
				this.cache.delete(oldestKey);
			}
		}
		this.cache.set(key, value);
	}

	has(key: K): boolean {
		return this.cache.has(key);
	}

	delete(key: K): boolean {
		return this.cache.delete(key);
	}

	clear(): void {
		this.cache.clear();
	}

	keys(): K[] {
		return Array.from(this.cache.keys());
	}

	values(): V[] {
		return Array.from(this.cache.values());
	}

	size(): number {
		return this.cache.size;
	}

	fromJSON(json: string): Result<boolean> {
		try {
			const parsed = JSON.parse(json);
			const now = Date.now();

			if (Array.isArray(parsed)) {
				parsed.forEach((entry) => {
					if (entry.expiresAt < now) {
						return;
					}

					this.cache.set(entry.id as K, entry);
				});
			} else {
				throw new Error("JSON is not an object");
			}

			console.log("cache loaded", this.cache);

			return ok(true);
		} catch (e) {
			return err(unwrapOr(e, new LruParseJsonError()));
		}
	}

	toJSON(): string {
		return JSON.stringify(Array.from(this.cache).map(([, value]) => value));
	}
}
